import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@biq/apex-ui';
import { InputAdornment } from '@mui/material';
import { Controller } from 'react-hook-form';
import SearchIcon from '@mui/icons-material/Search';
import { autoCompleteStyles } from '../../../lib';
import { CustomRenderOption } from 'pages/wallets-and-groups/components/create-or-edit-user-group/AutoCompleteGroupMembersField';

interface AutocompleteToFieldProps {
    optionsData: any[];
    control: any;
    error: any;
    fieldValue?: any[];
    setFieldValue: any;
    handleUnsavedChanges?: () => void;
}

export const AutocompleteToField = ({
    optionsData,
    control,
    error,
    fieldValue,
    setFieldValue,
    handleUnsavedChanges,
}: AutocompleteToFieldProps): React.JSX.Element => {
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [value, setValue] = useState<any[]>([]);

    useEffect(() => {
        if (fieldValue && fieldValue.length > 0) {
            if (fieldValue.length === optionsData.length) {
                setSelectAll(true);
            } else {
                setValue(fieldValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldValue]);

    useEffect(() => {
        if (selectAll) {
            setValue(optionsData);
            setFieldValue('to', optionsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectAll]);

    const handleSelectAll = (): void => {
        handleUnsavedChanges?.();
        if (selectAll) {
            setValue([]);
            setFieldValue('to', []);
        }
        setSelectAll((prev) => !prev);
    };

    const handleOnChange =
        (field: any) =>
        (_e: any, selectedValue: any, reason: any): void => {
            handleUnsavedChanges?.();
            if (reason === 'clear' || reason === 'removeOption')
                setSelectAll(false);
            if (
                reason === 'selectOption' &&
                selectedValue.length === optionsData.length
            )
                setSelectAll(true);
            setValue(selectedValue);
            field.onChange(selectedValue);
        };

    const deleteSelectAllChip = (): void => {
        setValue([]);
        setSelectAll(false);
        handleUnsavedChanges?.();
    };

    return (
        <Controller
            name={'to'}
            control={control}
            render={({ field }): React.JSX.Element => (
                <Autocomplete
                    {...field}
                    options={optionsData}
                    sx={autoCompleteStyles}
                    disableCloseOnSelect
                    getOptionLabel={(option: any): string => option.value}
                    isOptionEqualToValue={(option, value1): any =>
                        option.id === value1.id
                    }
                    onChange={handleOnChange(field)}
                    value={value}
                    selectAllFlag={selectAll}
                    isSelectAllOptionEnabled={true}
                    handleSelectAll={handleSelectAll}
                    selectAllChipLabel={'ALL USERS'}
                    selectAllFormControlLabel={'ALL USERS'}
                    onDeleteIconSelectAllChip={deleteSelectAllChip}
                    renderOption={(props, option, { selected }): any => (
                        <CustomRenderOption
                            props={props}
                            option={option}
                            selected={selected}
                        />
                    )}
                    multiple={true}
                    renderInput={(params): React.JSX.Element => (
                        <TextField
                            {...params}
                            size='medium'
                            label={'To'}
                            variant='outlined'
                            required
                            error={!!error}
                            errorText={error ? error.message : ''}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: [
                                    <InputAdornment
                                        key={'searchIcon'}
                                        position='start'
                                    >
                                        <SearchIcon />
                                    </InputAdornment>,
                                    params.InputProps.startAdornment
                                        ? params.InputProps.startAdornment
                                        : [],
                                ],
                            }}
                        />
                    )}
                />
            )}
        />
    );
};
